import React, { useState, useEffect } from 'react';
import { db } from '../firebase/config';
import { collection, getDocs, getDoc, setDoc, doc, serverTimestamp } from 'firebase/firestore';

const DEFAULTS = {
  inviteSubject: "Welcome to From Curious to Capable",
  inviteBody:
`Hey there!

Welcome to From Curious to Capable. I am genuinely glad you are here, and I want to make sure your first step into the program is smooth. This program is fully self-paced, so here is what to do to get started.

Step 1, create your login. Go to {{link}} and click Register. Use this company code when you create your account:

{{code}}

That code connects you to your program, so use it exactly as written.

Step 2, watch the welcome video. Once you are logged in, the first thing you will see is a short welcome video from me. It walks you through the portal, explains the AI Readiness Snapshot you are about to take, and tells you about the Final Exam at the end of the program. Watch it before you do anything else.

Step 3, take the Snapshot. After the welcome video, you will take a five-minute readiness check called the AI Readiness Snapshot. Be honest with yourself when you answer. There are no wrong answers, and your honest baseline now is what makes your finish line meaningful at the end of the program.

What to expect once you start.

This program is fully self-paced. You move through ten days of hands-on work in the portal on your own schedule. Each day has a short video and a workbook. Watch the video, then work through that day's workbook before you move on. You also have a full toolkit in the portal: the References tab gives you The AI Operator's Playbook, the AI Glossary, and the Agent-Building Platform Cheat Sheet to use anytime. The AI Use Case Finder helps you spot exactly where AI fits in your role, and your Prompt Library and the Wins Wall are there to support you as you go.

By the end of the ten days, you will have a working prompt library, a redesigned workflow with real time savings, an agent you designed, and a custom tool you built. Then you will take a Final Exam that opens up after your post-training Snapshot. Pass it, and your certificate appears in Account Settings.

If you run into any trouble with your login or the portal, reply to this email, and I will sort it out fast.

Talk soon!`,
  // Second invitation option for programs that run AI office hours. Fill in
  // the day, time, and location before sending.
  officeHoursSubject: "Welcome to From Curious to Capable",
  officeHoursBody:
`Hey there!

Welcome to From Curious to Capable, the AI training {{company}} invested in to help you learn to use AI in a significant way in your workday. It was built because most AI training tells you about AI without changing how you work. Every lesson here puts AI to work on your actual tasks, so by the end you are using AI instead of just hearing about it.

Adding yourself takes one step. Go to {{link}}, click Register, and enter this company code when you create your account:

{{code}}

Use it exactly as written. That code is what connects you to your company's program.

I am genuinely glad you are here, and I want to make sure your first step into the program is smooth. This program is fully self-paced, so here is what to do to get started.

Step 1, create your login. Go to {{link}} and click Register. Use this company code when you create your account:

{{code}}

That code connects you to your program, so use it exactly as written.

Step 2, watch the welcome video. Once you are logged in, the first thing you will see is a short welcome video from me. It walks you through the portal, explains the AI Readiness Snapshot you are about to take, and tells you about the Final Exam at the end of the program. Watch it before you do anything else.

Step 3, take the Snapshot. After the welcome video, you will take a five-minute readiness check called the AI Readiness Snapshot. Be honest with yourself when you answer. There are no wrong answers, and your honest baseline now is what makes your finish line meaningful at the end of the program.

Step 4, move at your own pace. There is no deadline on the ten days of videos and workbooks, and the participants who get the most from the program are the ones who fill their workbooks with real examples from their own work.

Step 5, join AI office hours on [day and time] at [location or link]. Bring your questions, your stuck moments, and your wins, and post those wins to the Wins Wall in your portal while you're at it.

What to expect once you start:

This program is fully self-paced. You move through ten days of hands-on work in the portal on your own schedule. Each day has a short video and a workbook. Watch the video, then work through that day's workbook before you move on. You also have a full toolkit in the portal: the References tab gives you The AI Operator's Playbook, the AI Glossary, and the Agent-Building Platform Cheat Sheet to use anytime. The AI Use Case Finder helps you spot exactly where AI fits in your role, and your Prompt and Agent Library as well as the Wins Wall are there to support you as you go.

By the end of the ten days, you will have a working prompt library, a redesigned workflow with real-time savings, an agent you designed, and a custom tool you built. Then you will take a Final Exam that opens up after your post-training Snapshot. Pass it, and your certificate appears in Account Settings.

If you run into any trouble with your login or the portal, reply to this email, and I will sort it out fast.

Talk soon!`,
  reminderSubject: "A nudge to keep going with your AI training",
  reminderBody:
`Hi {{name}},

Just a friendly nudge to keep going with your From Curious to Capable training. You can pick up right where you left off using the button below.

You've got this,
The Corporate Fixers`,
};

const EMPTY = { inviteSubject: '', inviteBody: '', officeHoursSubject: '', officeHoursBody: '', reminderSubject: '', reminderBody: '' };

export default function EmailTemplates({ user, mode = 'tcf' }) {
  const [codes, setCodes] = useState([]);
  const [loadingCodes, setLoadingCodes] = useState(true);
  const [code, setCode] = useState('');
  const [fields, setFields] = useState(EMPTY);
  const [loadingTpl, setLoadingTpl] = useState(false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  useEffect(() => {
    const loadCodes = async () => {
      try {
        if (mode === 'company' && user?.email) {
          const snap = await getDoc(doc(db, 'companyAdmins', user.email));
          const data = snap.exists() ? snap.data() : {};
          const list = Array.isArray(data.companyCodes) ? data.companyCodes : (data.companyCode ? [data.companyCode] : []);
          setCodes(list.map(c => ({ code: c, company: '' })));
          if (list.length === 1) { setCode(list[0]); }
        } else {
          const snap = await getDocs(collection(db, 'engagements'));
          setCodes(snap.docs.filter(d => d.data().active !== false).map(d => ({ code: d.id, company: d.data().company || '' })));
        }
      } catch (err) {
        setMsg({ type: 'error', text: 'Could not load your codes. ' + (err.message || '') });
      }
      setLoadingCodes(false);
    };
    loadCodes();
  }, [mode, user]);

  useEffect(() => {
    if (!code) { setFields(EMPTY); return; }
    const loadTpl = async () => {
      setLoadingTpl(true);
      try {
        const snap = await getDoc(doc(db, 'emailTemplates', code));
        const d = snap.exists() ? snap.data() : {};
        // Pre-fill every box with the wording that is actually in use (saved
        // custom wording if there is any, otherwise the default) so admins can
        // edit the real text instead of starting from a blank box.
        setFields({
          inviteSubject: d.inviteSubject || DEFAULTS.inviteSubject,
          inviteBody: d.inviteBody || DEFAULTS.inviteBody,
          officeHoursSubject: d.officeHoursSubject || DEFAULTS.officeHoursSubject,
          officeHoursBody: d.officeHoursBody || DEFAULTS.officeHoursBody,
          reminderSubject: d.reminderSubject || DEFAULTS.reminderSubject,
          reminderBody: d.reminderBody || DEFAULTS.reminderBody,
        });
      } catch (err) {
        setMsg({ type: 'error', text: 'Could not load this code\'s wording. ' + (err.message || '') });
      }
      setLoadingTpl(false);
    };
    loadTpl();
  }, [code]);

  const setField = (k, v) => setFields(prev => ({ ...prev, [k]: v }));

  const handleSave = async () => {
    if (!code) { setMsg({ type: 'error', text: 'Pick a program code first.' }); return; }
    setSaving(true);
    try {
      await setDoc(doc(db, 'emailTemplates', code), {
        inviteSubject: fields.inviteSubject.trim(),
        inviteBody: fields.inviteBody.trim(),
        officeHoursSubject: fields.officeHoursSubject.trim(),
        officeHoursBody: fields.officeHoursBody.trim(),
        reminderSubject: fields.reminderSubject.trim(),
        reminderBody: fields.reminderBody.trim(),
        updatedBy: user?.email || '',
        updatedAt: serverTimestamp(),
      }, { merge: true });
      setMsg({ type: 'success', text: 'Saved. New emails for this code will use this wording.' });
      setTimeout(() => setMsg(null), 4000);
    } catch (err) {
      setMsg({ type: 'error', text: 'Could not save. ' + (err.message || '') });
    }
    setSaving(false);
  };

  // Put the default wording back into the boxes (visible and editable).
  const resetField = (subjectKey, bodyKey) => {
    setField(subjectKey, DEFAULTS[subjectKey] || '');
    setField(bodyKey, DEFAULTS[bodyKey] || '');
  };

  return (
    <div style={s.container}>
      <div style={s.headerBlock}>
        <h1 style={s.heading}>Email Wording</h1>
        <p style={s.sub}>
          Change the words in the three emails for a program code: the standard invitation, the office hours
          invitation, and the reminder. Each box shows the wording currently in use, so edit right in the box and
          click Save. Made a mess? Click Reset to default wording to bring the original back. The Corporate Fixers
          branding, the access-code box, and the buttons are added automatically.
        </p>
      </div>

      <div style={s.card}>
        <label style={s.label}>Program code</label>
        {loadingCodes ? (
          <div style={s.hint}>Loading your codes…</div>
        ) : codes.length === 0 ? (
          <div style={{ ...s.msgBox, ...s.msgError }}>No program codes assigned to you yet.</div>
        ) : (
          <select style={{ ...s.input, maxWidth: '340px' }} value={code} onChange={e => setCode(e.target.value)}>
            <option value="">Choose a code…</option>
            {codes.map(c => <option key={c.code} value={c.code}>{c.code}{c.company ? ` — ${c.company}` : ''}</option>)}
          </select>
        )}
      </div>

      {code && !loadingTpl && (
        <>
          <div style={s.card}>
            <div style={s.sectionTitle}>Invitation email</div>
            <div style={s.tags}>Auto-fill tags you can use: <code>{'{{program}}'}</code> <code>{'{{company}}'}</code> <code>{'{{code}}'}</code></div>

            <label style={s.label}>Subject</label>
            <input style={s.input} value={fields.inviteSubject} onChange={e => setField('inviteSubject', e.target.value)} placeholder={DEFAULTS.inviteSubject} />

            <label style={s.label}>Message</label>
            <textarea style={{ ...s.input, minHeight: '150px', resize: 'vertical' }} value={fields.inviteBody} onChange={e => setField('inviteBody', e.target.value)} placeholder={DEFAULTS.inviteBody} />
            <button style={s.resetBtn} onClick={() => resetField('inviteSubject', 'inviteBody')}>Reset to default wording</button>
          </div>

          <div style={s.card}>
            <div style={s.sectionTitle}>Office hours invitation email</div>
            <div style={s.tags}>
              A second invitation for programs that run AI office hours. On the Invite Participants screen you choose
              which invitation goes out. Before you send this one, replace <code>[day and time]</code> and
              <code> [location or link]</code> with your office hours details.
            </div>
            <div style={s.tags}>Auto-fill tags you can use: <code>{'{{program}}'}</code> <code>{'{{company}}'}</code> <code>{'{{code}}'}</code></div>

            <label style={s.label}>Subject</label>
            <input style={s.input} value={fields.officeHoursSubject} onChange={e => setField('officeHoursSubject', e.target.value)} placeholder={DEFAULTS.officeHoursSubject} />

            <label style={s.label}>Message</label>
            <textarea style={{ ...s.input, minHeight: '150px', resize: 'vertical' }} value={fields.officeHoursBody} onChange={e => setField('officeHoursBody', e.target.value)} placeholder={DEFAULTS.officeHoursBody} />
            <button style={s.resetBtn} onClick={() => resetField('officeHoursSubject', 'officeHoursBody')}>Reset to default wording</button>
          </div>

          <div style={s.card}>
            <div style={s.sectionTitle}>Reminder email</div>
            <div style={s.tags}>Auto-fill tags you can use: <code>{'{{name}}'}</code> <code>{'{{company}}'}</code></div>

            <label style={s.label}>Subject</label>
            <input style={s.input} value={fields.reminderSubject} onChange={e => setField('reminderSubject', e.target.value)} placeholder={DEFAULTS.reminderSubject} />

            <label style={s.label}>Message</label>
            <textarea style={{ ...s.input, minHeight: '150px', resize: 'vertical' }} value={fields.reminderBody} onChange={e => setField('reminderBody', e.target.value)} placeholder={DEFAULTS.reminderBody} />
            <button style={s.resetBtn} onClick={() => resetField('reminderSubject', 'reminderBody')}>Reset to default wording</button>
          </div>

          <div style={s.saveRow}>
            <button style={s.saveBtn} onClick={handleSave} disabled={saving}>{saving ? 'Saving…' : 'Save wording'}</button>
            {msg && <div style={{ ...s.msgBox, ...(msg.type === 'error' ? s.msgError : s.msgSuccess), marginTop: 0 }}>{msg.text}</div>}
          </div>
        </>
      )}

      {loadingTpl && <div style={s.hint}>Loading…</div>}
    </div>
  );
}

const s = {
  container: { padding: '24px 32px', maxWidth: '900px' },
  headerBlock: { marginBottom: '24px' },
  heading: { fontFamily: 'Playfair Display, serif', fontSize: '26px', fontWeight: '900', color: '#1B2A4A', marginBottom: '8px' },
  sub: { fontSize: '13px', color: '#3D3D3D', fontWeight: '300', lineHeight: '1.6', maxWidth: '720px' },
  card: { background: '#FFFFFF', border: '1.5px solid #E8E6E2', padding: '22px 24px', marginBottom: '20px' },
  sectionTitle: { fontFamily: 'Playfair Display, serif', fontSize: '18px', fontWeight: '700', color: '#1B2A4A', marginBottom: '6px' },
  tags: { fontSize: '12px', color: '#8A8A8A', marginBottom: '8px' },
  label: { display: 'block', fontSize: '11px', fontWeight: '600', color: '#1B2A4A', letterSpacing: '0.4px', textTransform: 'uppercase', margin: '12px 0 5px' },
  input: {
    width: '100%', border: '1.5px solid #E8E6E2', padding: '10px 14px',
    fontFamily: 'DM Sans, sans-serif', fontSize: '13px', color: '#3D3D3D',
    background: '#fff', outline: 'none', boxSizing: 'border-box',
  },
  hint: { fontSize: '12px', color: '#8A8A8A' },
  resetBtn: { marginTop: '8px', background: 'none', border: 'none', padding: 0, color: '#185fa5', fontSize: '12px', fontWeight: '600', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif', textDecoration: 'underline' },
  saveRow: { display: 'flex', alignItems: 'center', gap: '14px', flexWrap: 'wrap' },
  saveBtn: { background: '#1B2A4A', color: '#fff', border: 'none', padding: '12px 26px', fontSize: '13px', fontWeight: '700', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif' },
  msgBox: { fontSize: '13px', padding: '10px 14px', marginTop: '16px', lineHeight: '1.5' },
  msgError: { background: '#fdecea', color: '#a32d2d' },
  msgSuccess: { background: '#eaf3de', color: '#3b6d11' },
};
